import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Building2, FileText, Download, Trash2, Calendar, Award, Eye,
  ArrowLeft, Search
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import { FREE_REPORT_GATE_MESSAGE } from '@/utils/planMessaging';
import { Input } from '@/components/ui/input';
import DeleteConfirmModal from '@/components/DeleteConfirmModal';

const flagEmoji = { CA: '🇨🇦', US: '🇺🇸' };

export default function ReportVault() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [deletingReport, setDeletingReport] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    setDownloading(report.id);
    try {
      const response = await axios.get(`${API}/reports/${report.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.property_name.replace(/\s+/g, '_')}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded!');
    } catch (error) {
      toast.error(error?.response?.data?.detail || FREE_REPORT_GATE_MESSAGE);
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteRequest = (reportId, e) => {
    e.stopPropagation();
    setDeleteReportId(reportId);
  };

  const handleDelete = async () => {
    if (!deleteReportId) return;
    setDeletingReport(true);
    try {
      await axios.delete(`${API}/reports/${deleteReportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report deleted');
      setDeleteReportId(null);
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    } finally {
      setDeletingReport(false);
    }
  };

  const filteredReports = reports.filter(report =>
    report.report_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.property_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getReportViewPath = (report) => {
    if (report?.source_type === 'analysis' && report?.analysis_type && report?.analysis_id) {
      return `/report/${report.analysis_type}/${report.analysis_id}`;
    }
    if (report?.analysis_type && report?.analysis_id) {
      return `/report/${report.analysis_type}/${report.analysis_id}`;
    }
    return null;
  };

  const handleView = (report) => {
    const viewPath = getReportViewPath(report);
    if (!viewPath) {
      toast.error('This report does not have a linked analysis view.');
      return;
    }
    navigate(viewPath);
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-slate-500/20 text-slate-400';
    if (grade.startsWith('A')) return 'bg-emerald-500/20 text-emerald-400';
    if (grade.startsWith('B')) return 'bg-cyan-500/20 text-cyan-400';
    if (grade.startsWith('C')) return 'bg-amber-500/20 text-amber-400';
    return 'bg-red-500/20 text-red-400';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-400">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-amber-500" />
                <span className="font-bold text-xl text-white font-['Manrope']">Report Vault</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white font-['Manrope'] mb-2">Your Reports</h1>
          <p className="text-slate-400">Download and manage your generated investment analysis reports</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-white"
            />
          </div>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-16 w-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No reports yet</h3>
            <p className="text-slate-400 mb-6">Generate reports from your deal analysis pages</p>
            <Button
              onClick={() => navigate('/deals')}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              Go to Deal Manager
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredReports.map((report) => (
              <div
                key={report.id}
                className="glass-card rounded-xl p-5 hover:border-slate-600 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-500/10">
                      <FileText className="h-6 w-6 text-amber-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white">
                          {report.report_name}
                        </h3>
                        <span className="text-lg">{flagEmoji[report.country_code]}</span>
                        {report.deal_score && (
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${getGradeColor(report.deal_score.grade)}`}>
                            {report.deal_score.grade}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span>{report.property_name}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(report.created_at).toLocaleDateString()}
                        </span>
                        <span>{formatFileSize(report.file_size)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleView(report)}
                      disabled={!getReportViewPath(report)}
                      className="text-slate-200 border-slate-600 hover:bg-slate-700/60"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDownload(report)}
                      disabled={downloading === report.id}
                      className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {downloading === report.id ? 'Downloading...' : 'Download'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteRequest(report.id, e)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <DeleteConfirmModal
        open={Boolean(deleteReportId)}
        onOpenChange={(open) => {
          if (!open && !deletingReport) {
            setDeleteReportId(null);
          }
        }}
        title="Delete report?"
        description="This removes the report from your vault. Monthly report usage does not reset when deleted."
        confirmLabel="Delete Report"
        cancelLabel="Cancel"
        destructive
        loading={deletingReport}
        onConfirm={handleDelete}
      />
    </div>
  );
}
